import { Check, ChevronsUpDown } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export type ComboboxOption = {
  value: string
  label: string
}

type ComboboxProps = {
  options: ComboboxOption[]
  value: string | string[]
  onChange: (value: string | string[]) => void
  multiple?: boolean
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  className?: string
  name?: string
}

export function Combobox({
  options,
  value,
  onChange,
  multiple = false,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No results found.',
  className,
  name,
}: ComboboxProps) {
  const [open, setOpen] = useState(false)

  const selectedValues = useMemo(() => {
    if (multiple) return Array.isArray(value) ? value : []
    return typeof value === 'string' && value ? [value] : []
  }, [multiple, value])

  const selectedLabels = options
    .filter((option) => selectedValues.includes(option.value))
    .map((option) => option.label)

  const sortedOptions = useMemo(() => {
    if (!multiple) return options
    return [...options].sort((a, b) => {
      const aSelected = selectedValues.includes(a.value) ? 0 : 1
      const bSelected = selectedValues.includes(b.value) ? 0 : 1
      if (aSelected !== bSelected) return aSelected - bSelected
      return a.label.localeCompare(b.label)
    })
  }, [multiple, options, selectedValues])

  const toggle = (nextValue: string) => {
    if (multiple) {
      const next = selectedValues.includes(nextValue)
        ? selectedValues.filter((item) => item !== nextValue)
        : [...selectedValues, nextValue]
      onChange(next)
      return
    }

    onChange(nextValue)
    setOpen(false)
  }

  const triggerLabel = (() => {
    if (selectedLabels.length === 0) return placeholder
    if (!multiple) return selectedLabels[0]
    if (selectedLabels.length === 1) return selectedLabels[0]
    return `${selectedLabels.length} selected`
  })()

  return (
    <>
      {name
        ? selectedValues.map((item) => <input key={item} type="hidden" name={name} value={item} />)
        : null}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn('w-full justify-between font-normal', className)}
          >
            <span className="truncate">{triggerLabel}</span>
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList className="max-h-[280px]">
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {sortedOptions.map((option) => {
                  const isSelected = selectedValues.includes(option.value)
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => toggle(option.value)}
                      className="cursor-pointer"
                    >
                      <Check className={cn('mr-2 size-4', isSelected ? 'opacity-100' : 'opacity-0')} />
                      <span className="truncate">{option.label}</span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  )
}
